/* Mini cart recommendations
   ===================================
   Adds a suggested product straight from the mini cart, and drives the
   slider's previous/next buttons.

   The mini cart wraps its contents in a <form>, so the regular
   card-product-add-to-cart snippet can't be reused here: a nested <form>
   is invalid HTML and the browser drops it. This posts to /cart/add.js
   directly and hands the response to the mini cart, the same way
   product-form.js does. */

if (!customElements.get("mini-cart-add")) {
  class MiniCartAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector("button");
      this.button?.addEventListener("click", this.onClick.bind(this));
    }

    onClick(event) {
      event.preventDefault();

      const miniCart = window.miniCart;
      if (!miniCart || this.button.classList.contains("loading")) {
        return;
      }

      this.setLoading(true);

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData();
      formData.append("id", this.dataset.variantId);
      formData.append("quantity", 1);
      formData.append(
        "sections",
        miniCart.getSectionsToRender().map((section) => section.section),
      );
      formData.append("sections_url", window.location.pathname);
      config.body = formData;

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            miniCart.setError(response.description || response.message);
            miniCart.updateSectionContents();
            return;
          }

          // Replaces the mini cart's markup, including this element: the
          // product that was just added drops out of the suggestions
          // because it's now in the cart.
          miniCart.renderContents(response);

          document.dispatchEvent(
            new CustomEvent("cart:item-add", {
              detail: { items: [response], context: "MiniCartRecommendations" },
            }),
          );

          publish(PUB_SUB_EVENTS.cartItemAdd, {
            source: "MiniCartRecommendations",
            items: [response],
          });
        })
        .catch((e) => {
          console.error(e);
          this.setLoading(false);
        });
    }

    setLoading(loading) {
      this.button.classList.toggle("loading", loading);
      this.button.setAttribute("aria-disabled", loading);
      this.querySelector(".button-overlay-spinner")?.classList.toggle("hidden", !loading);
    }
  }

  customElements.define("mini-cart-add", MiniCartAdd);
}

if (!customElements.get("mini-cart-recommendations")) {
  class MiniCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".mini-cart-recommendations-track");
      if (!this.track) return;

      this.querySelectorAll(".mini-cart-recommendations-nav-button").forEach((button) => {
        button.addEventListener("click", () => this.scrollBy(button.dataset.direction));
      });

      this.track.addEventListener("scroll", () => this.updateNavState());
      this.updateNavState();
    }

    scrollBy(direction) {
      // Card width alone leaves the gap behind, so each click would drift
      // further out of alignment with the snap points.
      const card = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card ? card.offsetWidth + gap : this.track.clientWidth;

      this.track.scrollBy({
        left: direction === "previous" ? -step : step,
        behavior: "smooth",
      });
    }

    updateNavState() {
      // A rounding slack of 1px keeps the end state reliable across zoom
      // levels and fractional widths.
      const atStart = this.track.scrollLeft <= 1;
      const atEnd =
        this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

      this.querySelector('[data-direction="previous"]')?.toggleAttribute("disabled", atStart);
      this.querySelector('[data-direction="next"]')?.toggleAttribute("disabled", atEnd);
    }
  }

  customElements.define("mini-cart-recommendations", MiniCartRecommendations);
}
